// ============================================================================
// utils_audio.ts
// Utilitaires audio — WebAudio mixer et chargement de buffers
// ============================================================================

export const initAudioMixer = (micStream: MediaStream | null): {
  ctx: AudioContext;
  dest: MediaStreamAudioDestinationNode;
} => {
  const ctx  = new AudioContext();
  const dest = ctx.createMediaStreamDestination();
  if (micStream && micStream.getAudioTracks().length > 0) {
    const micSrc  = ctx.createMediaStreamSource(micStream);
    const micGain = ctx.createGain();
    micGain.gain.value = 1;
    micSrc.connect(micGain);
    micGain.connect(dest);
  }
  return { ctx, dest };
};

/**
 * Buffer de bruit blanc bouclable — 2 s par défaut : assez long pour qu'aucune
 * périodicité ne soit perceptible, assez court pour un coût mémoire négligeable.
 *
 * Partagé par les deux usages du bruit inaudible : le plancher permanent du
 * mixer (AudioMixerPipeline.init) et la piste de remplissage de la publication
 * WHIP (createFillerAudioTrack ci-dessous).
 */
export const createWhiteNoiseBuffer = (ctx: BaseAudioContext, seconds = 2): AudioBuffer => {
  const frames = Math.round(ctx.sampleRate * seconds);
  const buffer = ctx.createBuffer(1, frames, ctx.sampleRate);
  const data   = buffer.getChannelData(0);
  for (let i = 0; i < frames; i++) data[i] = Math.random() * 2 - 1;
  return buffer;
};

/**
 * Piste audio de REMPLISSAGE — bruit blanc à gain inaudible.
 *
 * POURQUOI. La régie publie son programme vers MediaMTX en WHIP. Quand le
 * mixer n'a pas de piste audio exploitable, l'offre partait sans m-line audio
 * du tout : `webrtc_UUID` n'expose alors qu'une piste vidéo, et le microservice
 * ffmpeg en aval en meurt — son second étage (`ffmpeg_final`) fige son mapping
 * de streams au premier probe, l'antenne reste muette, puis la piste audio qui
 * REVIENT à la bascule suivante change le PMT du bus sous ses pieds et casse la
 * publication RTSP du path public. Constaté le 2026-08-23 sur Nasa TV : trois
 * morts de `ffmpeg_final` en 25 min, donc trois coupures d'antenne.
 *
 * Garantir une m-line audio TOUJOURS présente, et porteuse de vrais paquets RTP,
 * supprime le problème à la source.
 *
 * ⚠️ Bruit et NON silence : le silence numérique introduit un décalage
 * son/image sur le programme qui suit, là où un bruit à très faible gain n'en
 * produit aucun. C'est déjà le parti pris du reste du pipeline (gain 0.0001
 * pour garder les graphes WebAudio actifs dans Chrome, cf. AudioSourceRegistry
 * et AudioMixerPipeline) et de la mire du microservice ffmpeg (`anoisesrc
 * amplitude=0.0001`). 0.0001 ≈ -80 dBFS : inaudible.
 *
 * ⚠️ Le contexte créé ici est LOCAL à la piste retournée. Il ne doit jamais être
 * écrit dans audioMixerDestRef : un AudioContext concurrent de celui du provider
 * rendrait muets tous les sons/jingles branchés sur le pipeline (régression déjà
 * corrigée dans api_whip.ts).
 */
export const FILLER_AUDIO_GAIN = 0.0001;

export const createFillerAudioTrack = (): {
  track: MediaStreamTrack;
  dispose: () => void;
} => {
  const ctx  = new AudioContext({ sampleRate: 48000 });
  const dest = ctx.createMediaStreamDestination();

  const buffer = createWhiteNoiseBuffer(ctx);

  const src  = ctx.createBufferSource();
  src.buffer = buffer;
  src.loop   = true;

  const gain = ctx.createGain();
  gain.gain.value = FILLER_AUDIO_GAIN;

  src.connect(gain);
  gain.connect(dest);
  src.start();

  // La régie démarre toujours sur un geste utilisateur, le contexte ne devrait
  // pas être suspendu — mais un contexte suspendu ne produirait AUCUN paquet,
  // c'est-à-dire exactement la panne qu'on corrige ici.
  void ctx.resume().catch(() => {});

  const track = dest.stream.getAudioTracks()[0];

  return {
    track,
    dispose: () => {
      try { src.stop(); }  catch { /* déjà arrêté */ }
      try { track.stop(); } catch { /* déjà arrêtée */ }
      void ctx.close().catch(() => {});
    },
  };
};

export const preloadAudioBuffer = async (
  url: string,
  ctx: AudioContext
): Promise<AudioBuffer> => {
  let arrayBuf: ArrayBuffer;
  if (url.startsWith('data:')) {
    const base64 = url.split(',')[1];
    const binary = atob(base64);
    arrayBuf = new ArrayBuffer(binary.length);
    const view = new Uint8Array(arrayBuf);
    for (let i = 0; i < binary.length; i++) view[i] = binary.charCodeAt(i);
  } else {
    const res = await fetch(url);
    arrayBuf = await res.arrayBuffer();
  }
  return ctx.decodeAudioData(arrayBuf);
};
